import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Clock,
  Dumbbell,
  GraduationCap,
  HelpCircle,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Sparkles,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/breast-lift-surgery-mumbai";

const BREAST_LIFT_FAQS = [
  {
    question: "What is the difference between a breast lift and breast augmentation?",
    answer:
      "A breast lift repositions sagging breast tissue and the nipple to a more youthful level without significantly changing size. Breast augmentation increases size through implants without correcting position. Patients with both ptosis and volume loss often need both procedures combined. If you have sagging without wanting more size, a lift alone is appropriate. If you have small breasts without significant sagging, augmentation alone is appropriate.",
  },
  {
    question: "Will I lose nipple sensation after a breast lift?",
    answer:
      "Temporary changes in nipple sensation are common in the weeks following surgery as the nerves recover from the procedure. Permanent sensation loss is uncommon when the nipple is preserved on a tissue pedicle rather than detached. Dr. Jain uses pedicle techniques that maintain nipple blood supply and nerve connections in all standard mastopexy cases.",
  },
  {
    question: "Can I breastfeed after mastopexy?",
    answer:
      "In many cases, yes. Whether breastfeeding is preserved depends on the technique used and specifically whether the milk ducts remain intact. Patients who plan to breastfeed after surgery should discuss this explicitly during consultation so the surgical plan accounts for it. Periareolar and vertical techniques generally preserve ductal anatomy better than more extensive approaches.",
  },
  {
    question: "Will my breasts sag again after a lift?",
    answer:
      "Over time, natural aging, gravity, and any future weight or volume changes will continue to affect the breast. Results are long-lasting but not permanent. Patients who maintain a stable weight, avoid significant weight fluctuations, and wear proper support extend the longevity of their result. Most patients enjoy their result for 10 to 15 years before any additional intervention becomes relevant.",
  },
  {
    question: "How visible are the scars after breast lift surgery?",
    answer:
      "Scar extent depends on the technique used. A crescent lift produces minimal scarring around the areola border. An anchor lift produces more scar length including a horizontal component in the inframammary fold. All scars are positioned to be hidden under bras and swimwear. Scars mature and fade significantly over 12 to 18 months. Proper scar care including silicone sheeting and sun protection accelerates this process.",
  },
  {
    question: "How long does breast lift surgery recovery take?",
    answer:
      "Most patients return to desk work within 2 to 3 weeks. Physical activity and exercise resume at 6 to 8 weeks. The surgical bra is worn continuously for the first 6 weeks. Final breast shape and position settle over 3 to 6 months as residual swelling fully resolves.",
  },
  {
    question: "Should I get a breast lift before or after having children?",
    answer:
      "After. Pregnancy and breastfeeding alter breast volume and tissue in ways that affect the lifted result. A lift performed before completing a family produces a good result at the time but requires the breast tissue to undergo the changes of pregnancy, which frequently necessitates revision. Waiting until the family is complete means the lift result is maintained for longer without these intervening changes.",
  },
  {
    question: "Can a breast lift be combined with other procedures?",
    answer:
      "Yes. The most common combinations are mastopexy with augmentation for patients with both ptosis and volume loss, mastopexy with reduction for patients with large heavy breasts alongside sagging, and inclusion in a mommy makeover alongside abdominoplasty and liposuction for patients addressing multiple areas of post-pregnancy change in a single surgery.",
  },
];

const breastLiftStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Breast lift surgery (mastopexy)",
      alternateName: ["Mastopexy", "Breast lift", "Mastopexy surgery"],
      description:
        "Surgical elevation and reshaping of ptotic breasts with nipple-areola repositioning and excess skin removal. Distinct from breast augmentation (implant volume without correcting sagging); combined mastopexy-augmentation addresses both position and volume when needed.",
      procedureType: "Surgical",
      bodyLocation: {
        "@type": "AnatomicalStructure",
        name: "Breast",
      },
      followup:
        "Days 1–5: swelling, surgical bra. Weeks 1–2: light activity, suture care. Weeks 3–4: desk work. Weeks 6–8: full exercise. Months 3–6: final shape as swelling resolves.",
      howPerformed:
        "General anesthesia; upright markings for nipple position and skin resection; pedicle-based nipple repositioning; skin removal per technique (crescent, periareolar, vertical, or anchor); layered closure; surgical bra; 2–4 hours; same-day or overnight stay.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical" },
        {
          "@type": "PropertyValue",
          name: "Compared to breast augmentation",
          value: "Lift corrects sagging and nipple position; augmentation adds implant volume without lifting ptotic tissue",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: BREAST_LIFT_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

function BreastLiftJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breastLiftStructuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Breast", href: "/#services" },
    { name: "Breast Lift", href: "/breast-lift-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Breast</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Breast Lift Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Mastopexy by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Breast Lift" icon="🌸" size="hero"  imageSrc={HERO.breastLift} imageAlt="Breast lift"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is Breast Lift Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A breast lift, clinically called mastopexy, raises and reshapes sagging breasts, repositions the nipple-areola
              complex to a more youthful level, and removes excess skin that has stretched over time. It does not primarily
              change breast size. It corrects position and shape. Patients who want both lifting and increased volume combine
              mastopexy with breast augmentation. Patients whose breasts are large and heavy combine it with reduction.
            </p>
            <p>
              Breast ptosis, the clinical term for sagging, develops through several mechanisms: skin elasticity loss with age,
              volume and tissue changes from pregnancy and breastfeeding, the effects of gravity over time, and significant
              weight loss that removes volume faster than the skin contracts. The result is a breast that sits lower on the
              chest, with the nipple pointing downward rather than forward, and skin that has lost the firmness to maintain shape
              without support.
            </p>
            <p>
              Dr. Vicky Jain performs breast lift surgery using the technique matched to the patient&apos;s degree
              of ptosis. The choice of technique determines incision pattern, degree of correction achievable, and scar
              placement. Results are immediate in position and improve progressively as swelling resolves over 3 to 6 months.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Mastopexy</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Technique selection is the central decision in breast lift surgery. The degree of ptosis determines how much
                skin needs to be removed and how the breast tissue needs to be reshaped. Mild ptosis requires less intervention
                and shorter incisions. Severe ptosis requires the anchor technique, which removes significantly more skin and
                produces a more dramatic positional correction. Using an inadequate technique for a severe degree of ptosis
                produces a result that recurs faster and does not achieve the intended improvement.
              </p>
              <p>
                Dr. Jain marks the new nipple position, the degree of skin to be removed, and the reshaping plan before
                surgery with the patient standing upright. Breast tissue is assessed for volume distribution: where it sits,
                how much exists, and whether augmentation or reduction alongside lifting produces a more complete result.
                Patients who want to preserve the option of breastfeeding are counseled on technique selection, as some
                incision patterns preserve ductal anatomy better than others.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PtosisGradesSection() {
  const cards = [
    {
      title: "Grade I: Mild Ptosis",
      icon: CircleDot,
      copy: "The nipple sits at or near the level of the inframammary fold. The breast has lost firmness but retains reasonable position. A crescent or periareolar lift addresses mild ptosis with minimal incision length.",
    },
    {
      title: "Grade II: Moderate Ptosis",
      icon: Minus,
      copy: "The nipple sits below the inframammary fold but above the lowest point of the breast. A vertical or lollipop lift is appropriate, combining areolar incision with a vertical component for better reshaping.",
    },
    {
      title: "Grade III: Severe Ptosis",
      icon: ChevronDown,
      copy: "The nipple sits at or below the lowest contour of the breast and points downward. The anchor or inverted-T technique is required to remove sufficient skin and reshape the breast adequately. This is the most comprehensive lift and produces the most significant positional correction.",
    },
    {
      title: "Pseudoptosis",
      icon: HelpCircle,
      copy: "The breast volume has descended but the nipple position remains near normal. Often seen after significant weight loss or post-pregnancy volume deflation. May be better addressed through augmentation alone or augmentation with a minor lift rather than full mastopexy.",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="breast-ptosis-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Degrees of Breast Sagging and What They Mean for Surgery
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {cards.map((card, index) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]" data-testid={`ptosis-card-${index}`}>
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TECHNIQUE_TYPES = [
  {
    title: "Crescent Lift",
    body:
      "A small crescent of skin is removed along the upper border of the areola. Produces a very subtle lift of 1 to 2 centimeters. Appropriate only for mild ptosis where the nipple needs minor elevation and the breast tissue itself does not require significant reshaping. Leaves the least visible scar of all techniques.",
  },
  {
    title: "Periareolar Lift (Donut Lift)",
    body:
      "A circular incision is made around the entire areola. Skin is removed in a donut pattern and the areola is repositioned. Addresses mild ptosis and simultaneously reduces areola diameter when it has stretched. Scar is confined to the areola border and fades well.",
  },
  {
    title: "Vertical Lift (Lollipop)",
    body:
      "An incision circles the areola and extends vertically downward to the inframammary fold. Allows for more significant skin removal and better breast reshaping than the periareolar technique. Appropriate for moderate ptosis. Scar follows the lollipop pattern and fades over 12 to 18 months.",
  },
  {
    title: "Anchor Lift (Inverted-T)",
    body:
      "The most comprehensive technique. Incision circles the areola, extends vertically to the fold, and runs horizontally along the inframammary fold. Allows maximum skin removal and breast reshaping for patients with severe ptosis. Produces more scar length than other techniques but the horizontal scar sits within the natural breast fold and is not visible in clothing.",
  },
  {
    title: "Mastopexy with Augmentation",
    body:
      "Combines lifting with implant placement for patients who have both ptosis and volume loss. The two procedures planned together produce a result that is lifted in position and full in volume. When performed independently at different times, the result of the first procedure changes the anatomy for the second, making combined planning more predictable.",
  },
  {
    title: "Mastopexy with Reduction",
    body:
      "For patients with large, heavy breasts alongside ptosis. Breast tissue is removed to reduce weight and size while the breast is simultaneously lifted. Improves both aesthetics and the physical discomfort of breast weight.",
  },
];

function TechniquesSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="breast-lift-techniques-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Breast Lift Surgery</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {TECHNIQUE_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgeryPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Breast Lift Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 2 to 4 hours under general anesthesia. Dr. Jain marks the breast with the patient upright before
              surgery, identifying the new nipple position, the skin to be removed, and the tissue to be reshaped. These
              markings are the surgical plan. Precision here determines the final result.
            </p>
            <p>
              Through the incisions appropriate to the chosen technique, excess skin is removed, breast tissue is reshaped and
              lifted to sit higher on the chest wall, and the nipple-areola complex is repositioned to the marked level without
              detaching it from the underlying tissue. The nipple retains its blood supply and nerve connections through this
              pedicle technique, which is why sensation and breastfeeding function are usually preserved.
            </p>
            <p>
              The skin is closed in layers with sutures. A surgical bra or compression garment is applied immediately. Drains
              are used in some cases for the first 24 to 48 hours. Most patients go home the same day or after one night
              depending on whether additional procedures were performed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 5",
      description:
        "Swelling, bruising, and tightness across the chest. The surgical bra stays on continuously. Rest with limited arm movement overhead.",
      icon: BedDouble,
    },
    {
      period: "Weeks 1 to 2",
      description:
        "Light daily activity resumes. Sutures are checked and external sutures removed if non-absorbable. Most patients move around normally within the house.",
      icon: Monitor,
    },
    {
      period: "Weeks 3 to 4",
      description:
        "Most patients return to desk work and normal daily routine. Strenuous activity, heavy lifting, and vigorous exercise remain restricted.",
      icon: Dumbbell,
    },
    {
      period: "Weeks 6 to 8 and Beyond",
      description:
        "Full activity including exercise resumes. Scars are maturing and beginning to fade. Final breast position and shape settle over 3 to 6 months as residual swelling fully resolves.",
      icon: Sparkles,
    },
  ];

  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_AUG_ROWS = [
  {
    label: "Primary Goal",
    lift: "Correct position and shape of sagging breasts.",
    aug: "Increase breast volume and size.",
  },
  {
    label: "Implants Required",
    lift: "No. Works with existing tissue.",
    aug: "Yes. Implants are the procedure.",
  },
  {
    label: "Nipple Repositioning",
    lift: "Yes. Nipple is elevated to a more youthful position.",
    aug: "No. Nipple position is not changed.",
  },
  {
    label: "Volume Change",
    lift: "Minimal. May feel slightly fuller due to reshaping.",
    aug: "Significant. Volume is the primary outcome.",
  },
  {
    label: "Sagging Correction",
    lift: "Yes, the primary function.",
    aug: "No. Implants do not lift ptotic breasts.",
  },
  {
    label: "Best For",
    lift: "Post-pregnancy changes, age-related ptosis, significant weight loss.",
    aug: "Naturally small breasts, asymmetry, volume preference.",
  },
];

function VsAugmentationSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="breast-lift-vs-aug-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Breast Lift vs. Breast Augmentation: Which Do You Need?
        </h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <div className="hidden lg:grid lg:grid-cols-3 gap-0 border border-[#E8E2D9] bg-[#E8E2D9] min-w-[680px]" role="table" aria-label="Breast lift compared to breast augmentation">
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 font-medium" role="columnheader" />
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 text-center leading-tight" role="columnheader">
              Breast lift
            </div>
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 text-center leading-tight" role="columnheader">
              Breast augmentation
            </div>
            {VS_AUG_ROWS.map((row) => (
              <React.Fragment key={row.label}>
                <div className="bg-white p-3 text-sm font-medium text-deep-navy border-t border-[#E8E2D9]" role="rowheader">
                  {row.label}
                </div>
                <div className="bg-white p-3 text-sm text-deep-navy/70 leading-relaxed border-t border-[#E8E2D9]">{row.lift}</div>
                <div className="bg-white p-3 text-sm text-deep-navy/70 leading-relaxed border-t border-[#E8E2D9]">{row.aug}</div>
              </React.Fragment>
            ))}
          </div>
          <div className="hidden md:grid lg:hidden md:grid-cols-2 gap-4">
            {VS_AUG_ROWS.map((row) => (
              <div key={row.label} className="bg-white p-6 border border-[#E8E2D9] md:col-span-2">
                <h3 className="font-serif text-lg text-premium-purple mb-4">{row.label}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Breast lift</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.lift}</p>
                  </div>
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Breast augmentation</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.aug}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="md:hidden space-y-4">
            {VS_AUG_ROWS.map((row) => (
              <div key={row.label} className="bg-white p-6 border border-[#E8E2D9]">
                <h3 className="font-serif text-lg text-premium-purple mb-4">{row.label}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Breast lift</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.lift}</p>
                  </div>
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Breast augmentation</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.aug}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-deep-navy/60 text-center mt-8 max-w-3xl mx-auto leading-relaxed">
            Many patients need both. Volume loss and ptosis frequently occur together, particularly after pregnancy and
            breastfeeding. Augmentation alone in a ptotic breast adds volume without correcting position. A lift alone in a
            deflated breast improves position without restoring fullness. Combined mastopexy-augmentation addresses both.
          </p>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">
              Who Is a Good Candidate for Breast Lift Surgery?
            </h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are women who have completed their family or are not planning pregnancy in the near term, are at
              a stable weight, and have a specific concern with breast position, nipple level, or the shape and firmness of the
              breast. Pregnancy after breast lift is safe, but the volume and tissue changes of pregnancy and breastfeeding
              affect the lifted result. For patients who plan future pregnancies, delaying surgery until after completing their
              family preserves the result longer.
            </p>
            <p>
              Patients at a stable weight get more predictable results. Significant weight gain after surgery adds volume that
              the repositioned skin must accommodate, and significant weight loss removes volume the lift was shaped around.
              Neither invalidates the result entirely, but both affect how the outcome holds over time. Non-smokers or patients
              willing to stop smoking at least 4 weeks before surgery heal better and have lower complication rates. Patients
              with realistic expectations about scarring, understanding that all breast lift techniques involve incisions and
              that scars are managed but not eliminated, are the most consistently satisfied with their outcomes.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Breast" />;
}

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {BREAST_LIFT_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Breast Lift Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your degree of breast ptosis, nipple position, skin quality, and volume to
          determine which lift technique produces the appropriate correction for your anatomy and whether augmentation or
          reduction alongside lifting improves the overall result. You leave with a clear surgical plan, a realistic picture of
          outcome and scarring, and a complete understanding of the recovery process.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Breast Augmentation",
      description: "Add volume alongside positional correction for patients with both ptosis and size concerns.",
      href: "/breast-implant-surgery-mumbai",
    },
    {
      name: "Liposuction",
      description: "Body contouring to complement improved breast proportion and overall body balance.",
      href: "/liposuction-surgery-mumbai",
    },
    {
      name: "Mommy Makeover",
      description: "Combined post-pregnancy restoration addressing breast, abdomen, and body contour in a single surgery.",
      href: "/mommy-makeover-surgery-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BreastLiftPage() {
  return (
    <>
      <SEO
        title="Breast Lift Surgery | Mastopexy by Dr. Vicky Jain"
        description="Sagging breasts, downward-pointing nipples, and lost volume after pregnancy need structural correction. Dr. Vicky Jain performs mastopexy. Book today."
        canonical="/breast-lift-surgery-mumbai"
      />
      <BreastLiftJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <PtosisGradesSection />
      <TechniquesSection />
      <HowSurgeryPerformedSection />
      <RecoveryTimelineSection />
      <VsAugmentationSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
